import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { LogIn, ArrowLeft, Fingerprint, Mail, KeyRound, Loader2, Check, Eye, EyeOff } from "lucide-react";
import { isBiometricAvailable, checkUserBiometric, loginWithBiometric } from "../utils/webauthn";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricUser, setBiometricUser] = useState(null);
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Password recovery states
  const resetToken = searchParams.get("reset_token");
  const [mode, setMode] = useState(resetToken ? "reset" : "login"); // "login" | "forgot" | "reset" | "reset_done"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  // Email verification states
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    const check = async () => {
      const available = await isBiometricAvailable();
      setBiometricSupported(available);
      if (available) {
        const savedUser = localStorage.getItem("biometric_username");
        if (savedUser) {
          const hasCred = await checkUserBiometric(savedUser);
          if (hasCred) setBiometricUser(savedUser);
          else localStorage.removeItem("biometric_username");
        }
      }
    };
    check();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Inserisci email/cellulare e password");
      return;
    }
    // Safety belt: normalizza al submit anche se l'input non l'avesse fatto
    const normalized = username.includes("@") || /[a-zA-Z]/.test(username)
      ? username.toLowerCase().trim()
      : username.replace(/\D/g, "");
    setLoading(true);
    try {
      const result = await login(normalized, password);
      if (result.needsSelection) { navigate("/scegli-societa", { replace: true }); return; }
      const { user } = result;
      toast.success("Bentornato!");
      if (user.ruolo === "admin") navigate("/");
      else navigate("/operatore");
    } catch (err) {
      try {
        const res = await axios.post(`${API}/venditore/auth/login`, { email: normalized, password });
        if (res.data?.access_token) {
          localStorage.setItem("venditore_token", res.data.access_token);
          localStorage.setItem("venditore_user", JSON.stringify(res.data.user));
          toast.success(`Bentornato, ${res.data.user.nome}!`);
          navigate("/venditore");
          return;
        }
      } catch {
        // Non e nemmeno un venditore
      }
      const detail = err?.response?.data?.detail;
      // ── Email non verificata: mostra banner + pulsante reinvia ──
      if (err?.response?.status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setUnverifiedEmail(detail.email || normalized);
        toast.error(detail.message || "Conferma la tua email per accedere.", { duration: 8000 });
        return;
      }
      if (err?.response?.status === 403 && detail) {
        toast.error(typeof detail === "string" ? detail : (detail.message || "Accesso non consentito"), { duration: 6000 });
      } else {
        toast.error("Credenziali non valide");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!biometricUser) return;
    setBiometricLoading(true);
    try {
      const result = await loginWithBiometric(biometricUser);
      loginWithToken(result.access_token, result.user, result.locali);
      toast.success("Accesso biometrico riuscito!");
      if (result.user.ruolo === "admin") navigate("/");
      else navigate("/operatore");
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Autenticazione annullata");
      } else if (err.message && err.message.includes("dispositivo registrato")) {
        toast.error(err.message, { duration: 6000 });
      } else {
        toast.error(err.message || "Errore accesso biometrico");
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail || resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      toast.success(res.data?.message || "Email inviata! Controlla la tua casella.");
      setResendCooldown(60);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && typeof detail === "string") {
        // Estrai i secondi dal messaggio se presenti, altrimenti default 60
        const m = detail.match(/(\d+)\s*secondi/);
        if (m) setResendCooldown(parseInt(m[1], 10));
        toast.error(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore nell'invio. Riprova.");
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Inserisci la tua email"); return; }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Email inviata! Controlla la tua casella.");
    } catch {
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetLoading) return; // previene double-submit
    if (newPassword.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    if (newPassword !== confirmPassword) { toast.error("Le password non coincidono"); return; }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: newPassword });
      setMode("reset_done");
      // Rimuove ?reset_token=... dall'URL così se l'utente ricarica non riprova
      navigate("/login", { replace: true });
      toast.success("Password aggiornata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il reset password");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center p-4" style={{ background: "linear-gradient(145deg, #F0EFFE 0%, #F5F5FA 50%, #FFF0F4 100%)" }}>
      <div className="w-full max-w-sm relative z-10 bg-white rounded-[28px] p-8" style={{ boxShadow: "0 8px 40px rgba(108,99,255,0.12), 0 1px 0 rgba(255,255,255,0.8) inset" }}>
        <div className="text-center mb-8">
          <button onClick={() => navigate("/")} className="inline-block cursor-pointer" data-testid="login-logo-home">
            <img src="/yollo-logo.png" alt="Yollo" className="mx-auto mb-4 hover:scale-105 transition-transform" style={{ width: "120px", height: "auto" }} />
          </button>
          <p className="text-sm mt-1.5" style={{ color: "#9CA3AF" }}>
            {mode === "login" && "Accedi a Yollo"}
            {mode === "forgot" && "Recupera la tua password"}
            {mode === "reset" && "Imposta una nuova password"}
            {mode === "reset_done" && "Password aggiornata!"}
          </p>
        </div>

        {/* ─── LOGIN MODE ─── */}
        {mode === "login" && (
          <>
            {biometricSupported && biometricUser && (
              <div className="mb-5">
                <button
                  type="button" onClick={handleBiometricLogin} disabled={biometricLoading}
                  className="w-full h-[52px] flex items-center justify-center gap-2.5 rounded-2xl font-semibold text-base text-white transition-all active:scale-[0.98] disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #4F46E5, #6C63FF)", boxShadow: "0 4px 16px rgba(79,70,229,0.35)" }}
                  data-testid="biometric-login-btn"
                >
                  {biometricLoading ? <span className="animate-pulse">Verifica in corso...</span> : <><Fingerprint className="h-5 w-5" /> Accedi con biometria</>}
                </button>
                <p className="text-xs text-center mt-2" style={{ color: "#6B7280" }}>
                  Accesso rapido per <span className="font-semibold" style={{ color: "#4F46E5" }}>{biometricUser}</span>
                </p>
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                  <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-gray-400">oppure con password</span></div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Banner email non verificata */}
              {unverifiedEmail && (
                <div
                  className="rounded-2xl p-4 border-2 border-amber-200 bg-amber-50"
                  data-testid="unverified-email-banner"
                >
                  <div className="flex items-start gap-2 mb-2">
                    <Mail className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-bold text-amber-800">
                        Email non confermata
                      </p>
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        Apri il link che ti abbiamo inviato a <strong>{unverifiedEmail}</strong> per attivare l'account.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading || resendCooldown > 0}
                    className="w-full h-9 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: "linear-gradient(135deg, #F59E0B, #FBBF24)" }}
                    data-testid="resend-verification-btn"
                  >
                    {resendLoading ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Invio...
                      </span>
                    ) : resendCooldown > 0 ? (
                      `Riprova tra ${resendCooldown}s`
                    ) : (
                      "Reinvia link di conferma"
                    )}
                  </button>
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-sm font-semibold" style={{ color: "#374151" }}>Email o n. cellulare</Label>
                <Input id="username" type="text" value={username} onChange={(e) => {
                    // Normalizza: se contiene @ → email lowercase; altrimenti → solo cifre (telefono)
                    const v = e.target.value;
                    if (v.includes("@") || /[a-zA-Z]/.test(v)) {
                      setUsername(v.toLowerCase().trim());
                    } else {
                      setUsername(v.replace(/\D/g, ""));
                    }
                  }}
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="es. mario@locale.com oppure 3491234567" className="h-13 text-sm rounded-2xl border-0 font-medium placeholder:font-normal lowercase"
                  style={{ height: "52px", backgroundColor: "#F5F5FA", color: "#1A1A1E" }} data-testid="login-username" autoComplete="username" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-semibold" style={{ color: "#374151" }}>Password</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" className="h-13 text-sm rounded-2xl border-0 font-medium pr-10"
                    style={{ height: "52px", backgroundColor: "#F5F5FA", color: "#1A1A1E" }} data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="toggle-password-visibility">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Forgot password link */}
              <div className="text-right">
                <button type="button" onClick={() => setMode("forgot")}
                  className="text-xs font-semibold hover:underline" style={{ color: "#6C63FF" }}
                  data-testid="forgot-password-link">
                  Password dimenticata?
                </button>
              </div>

              <div className="pt-1">
                <button type="submit" disabled={loading}
                  className="w-full h-[52px] flex items-center justify-center gap-2 rounded-2xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: "0 4px 16px rgba(108,99,255,0.35)", letterSpacing: "0.02em" }}
                  data-testid="login-submit"
                >
                  {loading ? <span className="animate-pulse">Accesso in corso...</span> : <><LogIn className="h-5 w-5" /> Accedi</>}
                </button>
              </div>
            </form>

            <p className="mt-6 text-xs text-center" style={{ color: "#D1D5DB" }}>
              Non hai un account?{" "}
              <button type="button" onClick={() => navigate("/register")} className="font-semibold hover:underline" style={{ color: "#6C63FF" }} data-testid="goto-register-link">Registrati</button>
            </p>
            <button type="button" onClick={() => navigate("/")} className="mt-4 flex items-center gap-1.5 mx-auto text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors" data-testid="login-back-home">
              <ArrowLeft className="h-3.5 w-3.5" /> Torna alla home
            </button>
          </>
        )}

        {/* ─── FORGOT PASSWORD MODE ─── */}
        {mode === "forgot" && (
          <>
            {!forgotSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-sm text-gray-500 text-center mb-2">Inserisci l'email associata al tuo account e ti invieremo un link per reimpostare la password.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="forgot-email" className="text-sm font-semibold" style={{ color: "#374151" }}>Email</Label>
                  <Input id="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value.toLowerCase().trim())}
                    inputMode="email" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    placeholder="mario@email.com" className="h-13 text-sm rounded-2xl border-0 font-medium placeholder:font-normal lowercase"
                    style={{ height: "52px", backgroundColor: "#F5F5FA", color: "#1A1A1E" }} data-testid="forgot-email-input" />
                </div>
                <button type="submit" disabled={forgotLoading}
                  className="w-full h-[52px] flex items-center justify-center gap-2 rounded-2xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: "0 4px 16px rgba(108,99,255,0.35)" }}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Mail className="h-5 w-5" /> Invia link di recupero</>}
                </button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                  <Mail className="h-8 w-8 text-emerald-600" />
                </div>
                <p className="text-sm text-gray-600">Abbiamo inviato un'email a <strong>{forgotEmail}</strong> con le istruzioni per reimpostare la password.</p>
                <p className="text-xs text-gray-400">Controlla anche la cartella spam. Il link scade tra 1 ora.</p>
              </div>
            )}

            <button type="button" onClick={() => { setMode("login"); setForgotSent(false); setForgotEmail(""); }}
              className="mt-6 flex items-center gap-1.5 mx-auto text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="forgot-back-login">
              <ArrowLeft className="h-3.5 w-3.5" /> Torna al login
            </button>
          </>
        )}

        {/* ─── RESET PASSWORD MODE ─── */}
        {mode === "reset" && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-gray-500 text-center mb-2">Scegli una nuova password per il tuo account.</p>
            <div className="space-y-1.5">
              <Label htmlFor="new-password" className="text-sm font-semibold" style={{ color: "#374151" }}>Nuova Password</Label>
              <div className="relative">
                <Input id="new-password" type={showNewPassword ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min. 8 caratteri" className="h-13 text-sm rounded-2xl border-0 font-medium pr-10"
                  style={{ height: "52px", backgroundColor: "#F5F5FA", color: "#1A1A1E" }} data-testid="reset-new-password" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-new-password-visibility"
                  aria-label={showNewPassword ? "Nascondi password" : "Mostra password"}>
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-sm font-semibold" style={{ color: "#374151" }}>Conferma Password</Label>
              <div className="relative">
                <Input id="confirm-password" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password" className="h-13 text-sm rounded-2xl border-0 font-medium pr-10"
                  style={{ height: "52px", backgroundColor: "#F5F5FA", color: "#1A1A1E" }} data-testid="reset-confirm-password" />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-confirm-password-visibility"
                  aria-label={showConfirmPassword ? "Nascondi password" : "Mostra password"}>
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={resetLoading}
              className="w-full h-[52px] flex items-center justify-center gap-2 rounded-2xl text-white font-semibold text-base transition-all active:scale-[0.98] disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: "0 4px 16px rgba(108,99,255,0.35)" }}
              data-testid="reset-submit-btn"
            >
              {resetLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><KeyRound className="h-5 w-5" /> Reimposta Password</>}
            </button>
            <button type="button" onClick={() => { setMode("login"); navigate("/login", { replace: true }); }}
              className="mt-2 flex items-center gap-1.5 mx-auto text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="reset-back-login">
              <ArrowLeft className="h-3.5 w-3.5" /> Torna al login
            </button>
          </form>
        )}

        {/* ─── RESET DONE ─── */}
        {mode === "reset_done" && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="text-sm text-gray-600 font-semibold">La tua password e stata aggiornata con successo!</p>
            <p className="text-xs text-gray-400">Ora puoi accedere con le nuove credenziali.</p>
            <Button onClick={() => { setMode("login"); navigate("/login", { replace: true }); }}
              className="h-12 px-8 text-base font-bold rounded-2xl text-white"
              style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}
              data-testid="reset-done-login-btn">
              <LogIn className="h-4 w-4 mr-2" /> Accedi
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
